import { useFetcher } from "@remix-run/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { usePrintPreview } from "../hooks/useDetectPrint";

const prefersDarkMQ = "(prefers-color-scheme: dark)";
const getPreferredTheme = () =>
  typeof window !== "undefined"
    ? window.matchMedia(prefersDarkMQ).matches
      ? Theme.DARK
      : Theme.LIGHT
    : Theme.LIGHT;

enum Theme {
  DARK = "dark",
  LIGHT = "light",
}
const themes: Array<Theme> = Object.values(Theme);
export function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && themes.includes(value as Theme);
}

type ThemeContextType = [Theme | null, Dispatch<SetStateAction<Theme | null>>];

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function ThemeProvider({
  children,
  specifiedTheme,
}: {
  children: ReactNode;
  specifiedTheme: Theme | null;
}) {
  const [theme, setTheme] = useState<Theme | null>(() => {
    if (specifiedTheme && themes) {
      if (themes.includes(specifiedTheme)) {
        return specifiedTheme;
      } else {
        return null;
      }
    }

    return getPreferredTheme();
  });
  const persistTheme = useFetcher();

  const submitTheme = useCallback(
    (theme: string) => {
      persistTheme.submit(
        { theme },
        { action: "action/set-theme", method: "post" }
      );
    },
    [persistTheme]
  );

  const mountRun = useRef(false);
  const isPrintPreview = usePrintPreview();
  useEffect(() => {
    if (isPrintPreview) {
      return;
    }
    if (!mountRun.current) {
      mountRun.current = true;
      return;
    }
    if (!theme) {
      return;
    }

    submitTheme(theme);
  }, [isPrintPreview, theme]);

  useEffect(() => {
    if (isPrintPreview) {
      return;
    }
    const mediaQuery = window.matchMedia(prefersDarkMQ);
    const handleChange = () => {
      setTheme(mediaQuery.matches ? Theme.DARK : Theme.LIGHT);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [isPrintPreview]);

  return (
    <ThemeContext.Provider value={[theme, setTheme]}>
      {children}
    </ThemeContext.Provider>
  );
}

function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

export { Theme, ThemeProvider, useTheme };
